<script lang="ts">
  // Time-series line chart (inline SVG, no dependencies). One y-axis only; several
  // series share a unit. Target band + attention line come from the tracking spec;
  // era shading and medication markers give the clinical context. Hover shows a
  // crosshair and a tooltip listing every series at the nearest visit.
  import { fmtDate, fmtNum } from '../format.ts';

  export interface Pt { t: number; y: number; note?: string }
  export interface Series { key: string; label: string; color: string; points: Pt[] }
  export interface Marker { t: number; label: string; tone?: 'info' | 'warn' | 'danger' | 'good' }
  export interface Era { start: number; end: number | null; label: string }

  let {
    series, unit = '', decimals = 0, height = 220, targetBand = null, attentionLine = null,
    eras = [], markers = [], domain = null, title = '', subtitle = '', yMin = null, yMax = null,
  }: {
    series: Series[]; unit?: string; decimals?: number; height?: number;
    targetBand?: { low?: number; high?: number; label?: string } | null;
    attentionLine?: { value: number; label: string } | null;
    eras?: Era[]; markers?: Marker[]; domain?: [number, number] | null;
    title?: string; subtitle?: string; yMin?: number | null; yMax?: number | null;
  } = $props();

  let width = $state(640);
  let hoverT = $state<number | null>(null);
  let hoverX = $state(0);
  const M = { l: 48, r: 64, t: 22, b: 28 };
  const innerW = $derived(Math.max(10, width - M.l - M.r));
  const innerH = $derived(height - M.t - M.b);

  const all = $derived(series.flatMap((s) => s.points));
  const tMin = $derived(domain ? domain[0] : Math.min(...all.map((p) => p.t)));
  const tMax = $derived(domain ? domain[1] : Math.max(...all.map((p) => p.t)));

  function niceStep(span: number, n = 4): number {
    const raw = span / n; const p = Math.pow(10, Math.floor(Math.log10(raw))); const f = raw / p;
    return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
  }
  const yDom = $derived.by(() => {
    const ys = all.map((p) => p.y);
    if (targetBand?.low != null) ys.push(targetBand.low);
    if (targetBand?.high != null) ys.push(targetBand.high);
    if (attentionLine) ys.push(attentionLine.value);
    let lo = Math.min(...ys), hi = Math.max(...ys);
    if (!Number.isFinite(lo)) { lo = 0; hi = 1; }
    const pad = (hi - lo || 1) * 0.12;
    lo -= pad; hi += pad;
    if (yMin != null) lo = yMin; if (yMax != null) hi = yMax;
    const step = niceStep(hi - lo);
    lo = Math.floor(lo / step) * step; hi = Math.ceil(hi / step) * step;
    const ticks: number[] = []; for (let v = lo; v <= hi + 1e-9; v += step) ticks.push(Number(v.toFixed(6)));
    return { lo, hi, ticks };
  });
  const x = (t: number) => M.l + ((t - tMin) / Math.max(1, tMax - tMin)) * innerW;
  const y = (v: number) => M.t + (1 - (v - yDom.lo) / (yDom.hi - yDom.lo || 1)) * innerH;

  // X ticks sized to the pixel width: years for long spans, months for a year or
  // two, days for a short home-glucose window — never more labels than fit.
  const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const xTicks = $derived.by(() => {
    const out: { t: number; label: string }[] = [];
    const a = new Date(tMin), b = new Date(tMax);
    const spanDays = (tMax - tMin) / 86400000;
    const fit = Math.max(2, Math.floor(innerW / 48));
    if (spanDays > 730) {
      const years = spanDays / 365.25;
      const stepY = Math.max(1, Math.ceil(years / fit));
      for (let yr = a.getUTCFullYear() + 1; yr <= b.getUTCFullYear(); yr += stepY) { const t = Date.UTC(yr, 0, 1); if (t >= tMin && t <= tMax) out.push({ t, label: String(yr) }); }
    } else if (spanDays > 60) {
      const months: { t: number; label: string }[] = [];
      for (let yr = a.getUTCFullYear(); yr <= b.getUTCFullYear(); yr++) for (let mo = 0; mo < 12; mo++) { const t = Date.UTC(yr, mo, 1); if (t >= tMin && t <= tMax) months.push({ t, label: `${MO[mo]} ${String(yr).slice(2)}` }); }
      const step = Math.max(1, Math.ceil(months.length / fit));
      out.push(...months.filter((_, i) => i % step === 0));
    } else {
      const step = Math.max(1, Math.ceil(spanDays / fit));
      const d0 = Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate());
      for (let t = d0; t <= tMax; t += step * 86400000) { if (t >= tMin) { const d = new Date(t); out.push({ t, label: `${d.getUTCDate()} ${MO[d.getUTCMonth()]}` }); } }
    }
    return out;
  });

  const paths = $derived(series.map((s) => ({ s, d: s.points.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.y).toFixed(1)}`).join(' ') })));
  const showDots = $derived(all.length <= 220);

  // Hover: nearest point in time across all series.
  const times = $derived([...new Set(all.map((p) => p.t))].sort((a, b) => a - b));
  function onMove(e: MouseEvent) {
    const rect = (e.currentTarget as SVGElement).getBoundingClientRect();
    const px = e.clientX - rect.left;
    const t0 = tMin + ((px - M.l) / innerW) * (tMax - tMin);
    let best = times[0], bd = Infinity;
    for (const t of times) { const d = Math.abs(t - t0); if (d < bd) { bd = d; best = t; } }
    hoverT = best; hoverX = x(best);
  }
  const hoverRows = $derived(hoverT == null ? [] : series.map((s) => {
    const p = s.points.find((q) => q.t === hoverT) ?? s.points.reduce<Pt | null>((acc, q) => (Math.abs(q.t - hoverT!) < 15 * 86400000 && (!acc || Math.abs(q.t - hoverT!) < Math.abs(acc.t - hoverT!)) ? q : acc), null);
    return { s, p };
  }).filter((r) => r.p));
  const hoverMarkers = $derived(hoverT == null ? [] : markers.filter((m) => Math.abs(m.t - hoverT!) < 20 * 86400000));
  const tipLeft = $derived(hoverX > width * 0.6);
  const tone = (t?: string) => (t === 'danger' ? 'var(--danger)' : t === 'warn' ? 'var(--warn)' : t === 'good' ? 'var(--ok)' : 'var(--text-3)');
</script>

<div class="chart" bind:clientWidth={width}>
  {#if title || series.length >= 2}
    <div class="head">
      {#if title}<div class="title"><span class="t-body ttl">{title}</span>{#if subtitle}<span class="t-foot sub">{subtitle}</span>{/if}</div>{/if}
      {#if series.length >= 2}
        <div class="legend" aria-label="Series">
          {#each series as s (s.key)}<span class="lg"><i style="background:{s.color}"></i>{s.label}</span>{/each}
        </div>
      {/if}
    </div>
  {/if}
  <div class="plot" style="height:{height}px">
    <svg {width} {height} role="img" aria-label={title || series.map((s) => s.label).join(', ')} onmousemove={onMove} onmouseleave={() => (hoverT = null)}>
      <!-- era shading -->
      {#each eras as e, i (e.label)}
        {@const x0 = Math.max(M.l, x(Math.max(e.start, tMin)))}
        {@const x1 = Math.min(M.l + innerW, x(e.end == null ? tMax : Math.min(e.end, tMax)))}
        {#if x1 > x0}
          <rect x={x0} y={M.t - 14} width={x1 - x0} height={innerH + 14} fill={i % 2 ? 'var(--surface-2)' : 'transparent'} opacity="0.7" />
          {#if x1 - x0 > e.label.length * 6.4}<text x={x0 + 4} y={M.t - 4} class="era">{e.label}</text>{/if}
        {/if}
      {/each}
      <!-- target band -->
      {#if targetBand && (targetBand.low != null || targetBand.high != null)}
        {@const top = y(targetBand.high ?? yDom.hi)}
        {@const bot = y(targetBand.low ?? yDom.lo)}
        <rect x={M.l} y={top} width={innerW} height={Math.max(0, bot - top)} fill="var(--ok)" opacity="0.09" />
        {#if targetBand.label}<text x={M.l + 4} y={bot - 4} class="bandlbl">{targetBand.label}</text>{/if}
      {/if}
      <!-- grid + y axis -->
      {#each yDom.ticks as v (v)}
        <line x1={M.l} x2={M.l + innerW} y1={y(v)} y2={y(v)} class="grid" />
        <text x={M.l - 6} y={y(v) + 3.5} class="tick" text-anchor="end">{fmtNum(v, decimals)}</text>
      {/each}
      {#each xTicks as t (t.t)}
        <text x={x(t.t)} y={height - 8} class="tick" text-anchor="middle">{t.label}</text>
      {/each}
      <text x={M.l - 6} y={M.t - 6} class="unit" text-anchor="end">{unit}</text>
      <!-- attention line -->
      {#if attentionLine}
        <line x1={M.l} x2={M.l + innerW} y1={y(attentionLine.value)} y2={y(attentionLine.value)} class="attn" />
        <text x={M.l + 4} y={y(attentionLine.value) - 4} class="attnlbl">{attentionLine.label}</text>
      {/if}
      <!-- markers -->
      {#each markers as mk, i (mk.t + mk.label + i)}
        <line x1={x(mk.t)} x2={x(mk.t)} y1={M.t + innerH - 10} y2={M.t + innerH} stroke={tone(mk.tone)} stroke-width="1.5" />
        <circle cx={x(mk.t)} cy={M.t + innerH - 12} r="2.6" fill={tone(mk.tone)} />
      {/each}
      <!-- series -->
      {#each paths as { s, d } (s.key)}
        <path {d} fill="none" stroke={s.color} stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
        {#if showDots}
          {#each s.points as p (p.t)}<circle cx={x(p.t)} cy={y(p.y)} r="3.4" fill={s.color} stroke="var(--surface)" stroke-width="2" />{/each}
        {/if}
        {#if s.points.length}
          {@const last = s.points[s.points.length - 1]}
          <text x={x(last.t) + 8} y={y(last.y) + 3.5} class="endlbl">{fmtNum(last.y, decimals)}</text>
        {/if}
      {/each}
      <!-- crosshair -->
      {#if hoverT != null}
        <line x1={hoverX} x2={hoverX} y1={M.t} y2={M.t + innerH} class="xhair" />
        {#each hoverRows as { s, p } (s.key)}<circle cx={x(p!.t)} cy={y(p!.y)} r="5" fill={s.color} stroke="var(--surface)" stroke-width="2" />{/each}
      {/if}
    </svg>
    {#if hoverT != null && (hoverRows.length || hoverMarkers.length)}
      <div class="tip" style={tipLeft ? `right:${width - hoverX + 10}px` : `left:${hoverX + 10}px`}>
        <div class="tipdate">{fmtDate(new Date(hoverT).toISOString())}</div>
        {#each hoverRows as { s, p } (s.key)}
          <div class="tiprow"><i style="background:{s.color}"></i><span>{s.label}</span><b class="num">{fmtNum(p!.y, decimals)} {unit}</b></div>
          {#if p!.note}<div class="tipnote">{p!.note}</div>{/if}
        {/each}
        {#each hoverMarkers as mk (mk.t + mk.label)}<div class="tipmk" style="color:{tone(mk.tone)}">● {mk.label}</div>{/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .chart { width: 100%; min-width: 0; }
  .head { display: flex; align-items: baseline; justify-content: space-between; gap: var(--space-3); flex-wrap: wrap; margin-bottom: 2px; }
  .title { display: flex; flex-direction: column; }
  .ttl { font-weight: 600; }
  .sub { color: var(--text-3); }
  .legend { display: flex; gap: var(--space-3); flex-wrap: wrap; font-size: var(--fs-foot); color: var(--text-2); }
  .lg { display: inline-flex; align-items: center; gap: 6px; }
  .lg i, .tiprow i { width: 10px; height: 10px; border-radius: 50%; display: inline-block; flex: 0 0 auto; }
  .plot { position: relative; }
  svg { display: block; overflow: visible; cursor: crosshair; }
  .grid { stroke: var(--line); stroke-width: 1; }
  .tick, .unit, .era, .bandlbl, .attnlbl, .endlbl { font-size: 10.5px; fill: var(--text-3); font-family: var(--font); }
  .endlbl { fill: var(--text-2); font-weight: 600; font-variant-numeric: tabular-nums; }
  .era { font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; }
  .bandlbl { fill: var(--ok); }
  .attn { stroke: var(--danger); stroke-width: 1; opacity: 0.55; }
  .attnlbl { fill: var(--danger); }
  .xhair { stroke: var(--text-3); stroke-width: 1; opacity: 0.5; }
  .tip { position: absolute; top: 8px; pointer-events: none; background: var(--surface); border: 1px solid var(--line-strong); border-radius: var(--radius-sm); padding: 6px 10px; font-size: var(--fs-foot); box-shadow: 0 4px 16px rgba(15, 20, 26, 0.12); min-width: 160px; z-index: 2; }
  .tipdate { color: var(--text-3); margin-bottom: 2px; }
  .tiprow { display: flex; align-items: center; gap: 6px; }
  .tiprow span { color: var(--text-2); flex: 1; }
  .tiprow b { color: var(--text); }
  .tipnote { color: var(--text-3); margin: 0 0 2px 16px; }
  .tipmk { margin-top: 3px; font-weight: 600; }
</style>
